"use client";

import { useIsMobile } from "./hooks/use-mobile";
import { Button } from "@/components/ui/buttons/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltips/tooltip";
import { queryActivePage } from "@/lib/activePageDom";
import { cn } from "@/lib/utils";
import { assertNever } from "@/packages/ts-utils";
import { notNull } from "@/utils/helpers";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { Menu, PanelLeft, X } from "lucide-react";
import * as React from "react";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "232px";
const SIDEBAR_WIDTH_ICON = "48px";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
// The panel's duration-200 width transition.
const SIDEBAR_WIDTH_TRANSITION_MS = 200;
// Outlasts the rail-hidden content's duration-150 fade-in.
const SIDEBAR_FADE_IN_MS = 200;
const SIDEBAR_PEEK_FADE_OUT_MS = 150;
// Menus and dialogs opened from a peeking panel render outside it, so the peek holds while one is open.
const SIDEBAR_PEEK_HOLDING_OVERLAY_SELECTOR =
  '[role="menu"][data-state="open"], [role="dialog"][data-state="open"], [role="listbox"][data-state="open"]';

export type SidebarPage =
  | "account"
  | "admin"
  | "billing"
  | "create"
  | "edit-chat"
  | "emails"
  | "members"
  | "research-library"
  | "dashboard"
  | "workspace";

type SidebarContext = {
  // Expanded while open or peeking.
  state: "expanded" | "collapsed";
  // Lags `state` on close until the panel has narrowed, so it closes over the open layout.
  isRail: boolean;
  open: boolean;
  // isInstant skips the width animation; the contents fade in instead.
  setOpen: (open: boolean, options?: { isInstant?: boolean }) => void;
  // A closed rail shows the open panel over the page while hovered, without moving the page.
  isPeeking: boolean;
  setIsPeeking: (isPeeking: boolean) => void;
  // The last resize skipped the width animation, as a peek's always does.
  isInstantResize: boolean;
  // Only for the moment of an instant open, so content mounted later, as on navigation, does not fade.
  isFadingIn: boolean;
  // Only for the moment of an instant close: rows' collapse transitions would otherwise play inside the already narrow rail.
  isSnappingClosed: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  showTogggle: boolean;
  setShowTogggle: (show: boolean) => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile({});
    const [openMobile, setOpenMobile] = React.useState(false);
    const [showTogggle, setShowTogggle] = React.useState(true);
    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const [isPeeking, _setIsPeeking] = React.useState(false);
    const [isInstantResize, setIsInstantResize] = React.useState(false);
    const [isFadingIn, setIsFadingIn] = React.useState(false);
    React.useEffect(() => {
      if (!isFadingIn) return;
      const timer = setTimeout(() => setIsFadingIn(false), SIDEBAR_FADE_IN_MS);
      return () => clearTimeout(timer);
    }, [isFadingIn]);
    const [isSnappingClosed, setIsSnappingClosed] = React.useState(false);
    React.useEffect(() => {
      if (!isSnappingClosed) return;
      const timer = setTimeout(
        () => setIsSnappingClosed(false),
        SIDEBAR_WIDTH_TRANSITION_MS
      );
      return () => clearTimeout(timer);
    }, [isSnappingClosed]);
    const setIsPeeking = React.useCallback((nextIsPeeking: boolean) => {
      setIsInstantResize(true);
      setIsFadingIn(nextIsPeeking);
      setIsSnappingClosed(!nextIsPeeking);
      _setIsPeeking(nextIsPeeking);
    }, []);
    const setOpen = React.useCallback(
      (
        value: boolean | ((value: boolean) => boolean),
        options?: { isInstant?: boolean }
      ) => {
        const openState = typeof value === "function" ? value(open) : value;
        // Pinning a peeking panel keeps what is on screen: nothing slides or fades.
        const isPinningPeek = isPeeking && openState;
        const isInstant = (options?.isInstant ?? false) || isPinningPeek;
        _setIsPeeking(false);
        setIsInstantResize(isInstant);
        setIsFadingIn(isInstant && openState && !isPinningPeek);
        setIsSnappingClosed(isInstant && !openState);
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        // This sets the cookie to keep the sidebar state.
        if (typeof document !== "undefined") {
          document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        }
      },
      [setOpenProp, open, isPeeking]
    );

    const toggleSidebar = React.useCallback(() => {
      return isMobile
        ? setOpenMobile((open) => !open)
        : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const isShown = open || isPeeking;
    const state = isShown ? "expanded" : "collapsed";

    const [settledShown, setSettledShown] = React.useState(isShown);
    React.useEffect(() => {
      const timer = setTimeout(
        () => setSettledShown(isShown),
        SIDEBAR_WIDTH_TRANSITION_MS
      );
      return () => clearTimeout(timer);
    }, [isShown]);
    const isRail = !isShown && (isInstantResize || !settledShown);

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        isRail,
        open,
        setOpen,
        isPeeking,
        setIsPeeking,
        isInstantResize,
        isFadingIn,
        isSnappingClosed,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        showTogggle,
        setShowTogggle,
      }),
      [
        state,
        isRail,
        open,
        setOpen,
        isPeeking,
        setIsPeeking,
        isInstantResize,
        isFadingIn,
        isSnappingClosed,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        showTogggle,
        setShowTogggle,
      ]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <div
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "group/sidebar-wrapper flex min-h-svh w-full",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
    mobileContentClassName?: string;
    mobileDismissible?: boolean;
    mobileFixed?: boolean;
    mobileRepositionInputs?: boolean;
  }
>(
  (
    {
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      className,
      mobileContentClassName,
      mobileDismissible = true,
      mobileFixed,
      mobileRepositionInputs,
      children,
      ...props
    },
    ref
  ) => {
    const {
      isMobile,
      state,
      isRail,
      isPeeking,
      isInstantResize,
      isFadingIn,
      isSnappingClosed,
      openMobile,
      setOpenMobile,
    } = useSidebar();
    const { panelRef: peekPanelRef, onPointerOver: onPeekPointerOver } =
      useSidebarPeek({
        isEnabled: collapsible === "icon" && !isMobile,
      });

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Drawer
          open={openMobile}
          onOpenChange={setOpenMobile}
          dismissible={mobileDismissible}
          fixed={mobileFixed}
          repositionInputs={mobileRepositionInputs}
        >
          <DrawerContent
            data-sidebar="sidebar"
            data-mobile="true"
            className={cn(
              "h-[90dvh] max-h-[90dvh] bg-sidebar p-0 text-sidebar-foreground",
              mobileContentClassName
            )}
          >
            {/* The sheet is a dialog, which needs a name for screen readers. */}
            <DrawerTitle className="sr-only">Navigation</DrawerTitle>
            <div className="flex h-full min-h-0 w-full flex-col">
              {children}
            </div>
          </DrawerContent>
        </Drawer>
      );
    }

    // The rail layout waits for the panel to narrow; offcanvas slides on this attribute, so it flips at once.
    const isCollapsedLayout = (() => {
      switch (collapsible) {
        case "icon":
          return isRail;
        case "offcanvas":
          return state === "collapsed";
        default:
          return assertNever(collapsible);
      }
    })();

    return (
      <div
        ref={ref}
        className={cn(
          "group peer hidden md:block text-sidebar-foreground",
          isSnappingClosed && "[&_*]:!transition-none"
        )}
        data-state={state}
        data-collapsible={isCollapsedLayout ? collapsible : ""}
        // Content the rail hides fades in on an instant open; the icons hold still.
        data-instant-open={isFadingIn ? "" : undefined}
        data-variant={variant}
        data-side={side}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "duration-200 relative h-svh w-[--sidebar-width] bg-transparent transition-[width] ease-linear",
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            // Narrows on data-state, which data-collapsible lags on close.
            collapsible === "icon" &&
              (variant === "floating" || variant === "inset"
                ? "group-data-[state=collapsed]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
                : "group-data-[state=collapsed]:w-[--sidebar-width-icon]"),
            isPeeking && "w-[--sidebar-width-icon]",
            isInstantResize && "transition-none"
          )}
        />
        <div
          ref={peekPanelRef}
          onPointerOver={onPeekPointerOver}
          className={cn(
            "duration-200 fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            (variant === "floating" || variant === "inset") && "p-2",
            collapsible === "icon" &&
              (variant === "floating" || variant === "inset"
                ? "group-data-[state=collapsed]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
                : "group-data-[state=collapsed]:w-[--sidebar-width-icon]"),
            className,
            // The page's own divider is under the panel while it peeks.
            isPeeking &&
              "z-40 border-r border-surface-quatenary shadow-floating",
            isInstantResize && "transition-none"
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex h-full w-full flex-col overflow-clip bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
          >
            {/* At the open width until the rail lands, so the panel reveals and covers rows instead of reflowing them. */}
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col group-data-[collapsible=icon]:w-full",
                variant === "floating" || variant === "inset"
                  ? "w-[calc(var(--sidebar-width)_-_theme(spacing.4))]"
                  : "w-[--sidebar-width]"
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
Sidebar.displayName = "Sidebar";

function useSidebarPeek({ isEnabled }: { isEnabled: boolean }) {
  const { open, isPeeking, setIsPeeking } = useSidebar();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const canPeek = isEnabled && !open;
  const fadeOutCopyRef = React.useRef<HTMLElement | null>(null);
  const hasLeftRef = React.useRef(false);
  const removeFadeOutCopy = React.useCallback(() => {
    fadeOutCopyRef.current?.remove();
    fadeOutCopyRef.current = null;
  }, []);
  React.useEffect(() => removeFadeOutCopy, [removeFadeOutCopy]);

  // The rail lands under a static copy of the open panel that fades out, so
  // the icons hold still and the wide rows cross-fade into the rail's.
  const fadeOutPeek = React.useCallback(() => {
    removeFadeOutCopy();
    const panel = panelRef.current;
    const group = panel?.parentElement;
    const container = group?.parentElement;
    const isReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (!panel || !group || !container || isReducedMotion) return;
    const groupCopy = group.cloneNode(false);
    const panelCopy = panel.cloneNode(true);
    if (
      !(groupCopy instanceof HTMLElement) ||
      !(panelCopy instanceof HTMLElement)
    ) {
      return;
    }
    // Not a peer, so the page's peer-data styles keep following the real panel.
    groupCopy.classList.remove("peer");
    groupCopy.removeAttribute("data-instant-open");
    groupCopy.inert = true;
    groupCopy.setAttribute("aria-hidden", "true");
    Object.assign(groupCopy.style, {
      position: "fixed",
      inset: "0 auto 0 0",
      zIndex: "40",
      pointerEvents: "none",
    });
    Object.assign(panelCopy.style, {
      boxShadow: "none",
      borderRightColor: "transparent",
    });
    // A clone restarts its CSS animations, so labels still fading in would flash; the copy keeps their current opacity.
    const elements = [panel, ...Array.from(panel.querySelectorAll("*"))];
    const elementCopies = [
      panelCopy,
      ...Array.from(panelCopy.querySelectorAll("*")),
    ];
    panel.getAnimations({ subtree: true }).forEach((animation) => {
      const target =
        animation.effect instanceof KeyframeEffect
          ? animation.effect.target
          : null;
      const elementCopy = target
        ? elementCopies[elements.indexOf(target)]
        : null;
      if (!target || !(elementCopy instanceof HTMLElement)) return;
      Object.assign(elementCopy.style, {
        opacity: getComputedStyle(target).opacity,
        animation: "none",
        transition: "none",
      });
    });
    groupCopy.append(panelCopy);
    container.append(groupCopy);
    // Clones start unscrolled; scroll offsets only apply once the copy is in the document.
    elements.forEach((element, index) => {
      const elementCopy = elementCopies[index];
      if (element.scrollTop === 0 && element.scrollLeft === 0) return;
      if (!(elementCopy instanceof HTMLElement)) return;
      elementCopy.scrollTop = element.scrollTop;
      elementCopy.scrollLeft = element.scrollLeft;
    });
    fadeOutCopyRef.current = groupCopy;
    groupCopy.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: SIDEBAR_PEEK_FADE_OUT_MS,
      easing: "linear",
    }).onfinish = () => {
      if (fadeOutCopyRef.current === groupCopy) removeFadeOutCopy();
    };
  }, [removeFadeOutCopy]);

  // Closing follows the pointer across the whole page, not the panel's own
  // leave event, so a menu opened from the panel can hold it.
  React.useEffect(() => {
    if (!isPeeking) return;
    const handlePointerMove = (event: PointerEvent) => {
      const isOverPanel =
        event.target instanceof Node &&
        (panelRef.current?.contains(event.target) ?? false);
      const hasHoldingOverlay = notNull(
        queryActivePage(SIDEBAR_PEEK_HOLDING_OVERLAY_SELECTOR)
      );
      // Pointer moves can arrive before the closed panel renders.
      if (!isOverPanel && !hasHoldingOverlay && !hasLeftRef.current) {
        hasLeftRef.current = true;
        fadeOutPeek();
        setIsPeeking(false);
      }
    };
    document.addEventListener("pointermove", handlePointerMove);
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isPeeking, setIsPeeking, fadeOutPeek]);

  return {
    panelRef,
    onPointerOver: (event: React.PointerEvent) => {
      // Ignore the rail overhang; pointerover fires again when the pointer reaches the panel body.
      if (
        event.target instanceof Element &&
        event.target.closest('[data-sidebar="rail"]')
      ) {
        return;
      }
      if (canPeek && !isPeeking && event.pointerType === "mouse") {
        // A copy still fading stays on top, so the labels' fade-in blends up from it instead of dipping.
        hasLeftRef.current = false;
        setIsPeeking(true);
      }
    },
  };
}

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar, openMobile } = useSidebar();
  // A phone gets the hamburger. Each icon reads its own state and CSS shows
  // one, so the first paint is right before the client knows which device
  // it is on.
  const SheetIcon = openMobile ? X : Menu;

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="smIcon"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
      className={cn(className)}
    >
      <PanelLeft className="size-4 max-md:hidden" strokeWidth={1.7} />
      <SheetIcon className="size-4 md:hidden" strokeWidth={1.7} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { enableCollapse?: boolean }
>(({ className, enableCollapse = true, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={enableCollapse ? toggleSidebar : undefined}
      title={enableCollapse ? "Toggle Sidebar" : undefined}
      className={cn(
        // The outline is off at rest as well as on focus. Turning it off only
        // on focus changes it at that moment, and transition-all then fades a
        // text-coloured outline out over the rail: two dark lines down the
        // sidebar's edge. The rail is pointer-only, so it needs no focus ring.
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 outline-none transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  );
});
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  // A div, not <main>: pages render their own main landmark via Page.Card.
  return (
    <div
      ref={ref}
      className={cn(
        // min-w-0: a wide page scrolls inside the inset instead of widening
        // it and carrying the header off a phone's screen.
        "relative flex min-h-svh min-w-0 flex-1 flex-col bg-surface-secondary",
        "peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-2xl md:peer-data-[variant=inset]:shadow",
        className
      )}
      {...props}
    />
  );
});
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
});
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
});
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-sidebar-border", className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        // No reserved gutter: where scrollbars take space, it would leave more room right of the rows than left.
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  );
});
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn("w-full text-sm", className)}
    {...props}
  />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-1", className)}
    {...props}
  />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn("group/menu-item relative", className)}
    {...props}
  />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

// Closing, rows shrink into the rail's square by clip-path at the panel's pace, which leaves their layout and text alone; opening, they take their open size at once.
const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring [clip-path:inset(0)] group-data-[state=collapsed]:[clip-path:inset(0_calc(100%-2rem)_0_0_round_theme(borderRadius.listen-8))] group-data-[state=collapsed]:transition-[clip-path,padding,border-width] group-data-[state=collapsed]:duration-200 group-data-[state=collapsed]:ease-linear hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-inset active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = "default",
      size = "default",
      tooltip,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== "collapsed" || isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const LABEL_REVEAL_DELAY_MS = 400;
const LABEL_REVEAL_PX_PER_SECOND = 120;

// While the row around a truncated label is hovered, the label scrolls left to
// show its end and carries data-revealing, which the label's styles use to
// drop the ellipsis. The row is the nearest menu button or [data-label-row].
// Scrolling rather than a transform: a transform animation moves a rasterised
// text layer at fractional offsets and blurs the glyphs, whereas scroll
// offsets snap to device pixels and the text repaints crisp.
function useSidebarLabelReveal(labelRef: React.RefObject<HTMLElement | null>) {
  const delayRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const frameRef = React.useRef<number>(undefined);

  React.useEffect(() => {
    const label = labelRef.current;
    if (!label) return;
    const row =
      label.closest("[data-sidebar=menu-button], [data-label-row]") ?? label;

    const cancelPending = () => {
      clearTimeout(delayRef.current);
      if (frameRef.current !== undefined)
        cancelAnimationFrame(frameRef.current);
      delayRef.current = undefined;
      frameRef.current = undefined;
    };
    const reset = () => {
      cancelPending();
      label.scrollLeft = 0;
      label.removeAttribute("data-revealing");
    };
    const reveal = () => {
      const distance = label.scrollWidth - label.clientWidth;
      if (distance <= 0) return;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (prefersReducedMotion) {
        label.scrollLeft = distance;
        return;
      }
      const durationMs = (distance / LABEL_REVEAL_PX_PER_SECOND) * 1000;
      const startedAt = performance.now();
      const step = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / durationMs);
        label.scrollLeft = distance * progress;
        if (progress < 1) frameRef.current = requestAnimationFrame(step);
      };
      frameRef.current = requestAnimationFrame(step);
    };
    const start = () => {
      cancelPending();
      label.setAttribute("data-revealing", "");
      delayRef.current = setTimeout(reveal, LABEL_REVEAL_DELAY_MS);
    };

    row.addEventListener("pointerenter", start);
    row.addEventListener("pointerleave", reset);
    return () => {
      row.removeEventListener("pointerenter", start);
      row.removeEventListener("pointerleave", reset);
      cancelPending();
    };
  }, [labelRef]);
}

// For content the rail drops: it fades at the panel's pace while the panel closes over it, then leaves the layout once the rail lands. An instant open fades it back in.
const SIDEBAR_RAIL_HIDDEN_CLASS =
  "group-data-[collapsible=icon]:hidden group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:transition-opacity group-data-[state=collapsed]:duration-200 group-data-[state=collapsed]:ease-linear group-data-[instant-open]:animate-in group-data-[instant-open]:fade-in-0 group-data-[instant-open]:duration-150 group-data-[instant-open]:ease-out";

// A row's text: truncates at rest, reveals its end while the row is hovered.
function SidebarMenuLabel({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  useSidebarLabelReveal(labelRef);

  return (
    <span
      ref={labelRef}
      data-sidebar="menu-label"
      className={cn(
        // !: the button's [&>span:last-child]:truncate outranks a data variant.
        "min-w-0 flex-1 truncate data-[revealing]:![text-overflow:clip]",
        SIDEBAR_RAIL_HIDDEN_CLASS,
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
));
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 flex-1 max-w-[--skeleton-width]"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className
    )}
    {...props}
  />
));
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
  }
>(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  SIDEBAR_RAIL_HIDDEN_CLASS,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLabel,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
  useSidebarLabelReveal,
};
